import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Modal,
  Paper,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAcknowledgeAccessMutation } from '@bvi/dashboard/entities/user-auth/api-slice';

import { styles } from './styles';

export const AccessAcknowledgementModal: FC = () => {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);
  const [acknowledgeAccess, { isLoading }] = useAcknowledgeAccessMutation();

  const handleContinue = async () => {
    if (!agreed) {
      return;
    }

    await acknowledgeAccess().unwrap();
  };

  return (
    <Modal
      open
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
    >
      <Box component={Paper} sx={styles.modalPaper}>
        <Typography variant="h5" sx={styles.title}>
          {t('auth.accessAcknowledgement.title')}
        </Typography>
        <Typography sx={styles.intro}>
          {t('auth.accessAcknowledgement.intro')}
        </Typography>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>
            {t('auth.accessAcknowledgement.authorizedUse.title')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.authorizedUse.body1')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.authorizedUse.body2')}
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>
            {t('auth.accessAcknowledgement.individualAccess.title')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.individualAccess.body1')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.individualAccess.body2')}
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>
            {t('auth.accessAcknowledgement.limitedSharing.title')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.limitedSharing.body')}
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>
            {t('auth.accessAcknowledgement.protection.title')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.protection.intro')}
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li>{t('auth.accessAcknowledgement.protection.items.resell')}</li>
            <li>{t('auth.accessAcknowledgement.protection.items.scrape')}</li>
            <li>
              {t('auth.accessAcknowledgement.protection.items.reproduce')}
            </li>
            <li>{t('auth.accessAcknowledgement.protection.items.compete')}</li>
          </Box>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>
            {t('auth.accessAcknowledgement.confidential.title')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.confidential.body1')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.confidential.body2')}
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>
            {t('auth.accessAcknowledgement.judgment.title')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.judgment.body1')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.judgment.body2')}
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>
            {t('auth.accessAcknowledgement.acknowledgement.title')}
          </Typography>
          <Typography sx={styles.sectionBody}>
            {t('auth.accessAcknowledgement.acknowledgement.intro')}
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li>
              {t('auth.accessAcknowledgement.acknowledgement.items.authorized')}
            </li>
            <li>
              {t('auth.accessAcknowledgement.acknowledgement.items.comply')}
            </li>
            <li>{t('auth.accessAcknowledgement.acknowledgement.items.msa')}</li>
          </Box>
        </Box>

        <FormControlLabel
          sx={styles.checkboxRow}
          control={
            <Checkbox
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
              disabled={isLoading}
            />
          }
          label={t('auth.accessAcknowledgement.agreeLabel')}
        />

        <Box sx={styles.actions}>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            disabled={!agreed}
            onClick={handleContinue}
          >
            {t('auth.accessAcknowledgement.continue')}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};
